/**
 * Keyboard-initiated actions never animate (ADR-1033). This tracks whether the last thing the person did was press a
 * key or use a pointer, and tells the CSS (base.css) about it:
 *
 * - `<html data-input="keyboard" | "pointer">`: leaving by keyboard (Esc, Enter on an item) has no exit animation;
 * - `data-instant` on a layer that mounts while the last input was a key: it has no enter animation either.
 *
 * A layer is a direct child of `<body>` (Radix portals there: a popper's wrapper, a dialog and its backdrop), so one
 * observer on the body's own children is enough; nothing watches the whole tree.
 */
type Input = 'keyboard' | 'pointer';

let last: Input = 'pointer';
let installed = false;

/** True when the last input was a key press (a shortcut, Enter or Space on a trigger, typing). */
export const isKeyboardInput = () => last === 'keyboard';

function set(input: Input) {
  if (last === input) return;
  last = input;
  document.documentElement.dataset.input = input;
}

/** Marks an element that opened because of a key press, so its enter animation is skipped. */
export function stampInstant(el: Element | null) {
  if (el && last === 'keyboard') el.setAttribute('data-instant', '');
}

const OPEN_STATES = new Set(['open', 'delayed-open', 'instant-open']);

function stampLayer(node: Node) {
  if (!(node instanceof HTMLElement)) return;
  const target = node.hasAttribute('data-radix-popper-content-wrapper') ? node.firstElementChild : node;
  const state = target?.getAttribute('data-state');
  if (target && state && OPEN_STATES.has(state)) stampInstant(target);
}

/** Starts tracking. Safe to call more than once; does nothing without a document (server rendering, unit tests). */
export function installMotionInput() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.documentElement.dataset.input = last;
  // Capture phase and first in line, so the attribute is right before any handler (Radix's Esc included) reacts.
  // Bare modifier keys (Shift, Ctrl, Alt, Meta) don't count: a held key isn't an action.
  window.addEventListener('keydown', (e) => !['Shift', 'Control', 'Alt', 'Meta'].includes(e.key) && set('keyboard'), true);
  window.addEventListener('pointerdown', () => set('pointer'), true);
  window.addEventListener('pointermove', () => set('pointer'), { capture: true, passive: true });
  new MutationObserver((records) => {
    for (const r of records) r.addedNodes.forEach(stampLayer);
  }).observe(document.body, { childList: true });
}
